import React, { useEffect, useState, useCallback } from 'react';
import { 
  LogOut, Wifi, WifiOff,
  LayoutDashboard, HeartHandshake, UserRoundPlus, Stethoscope, Pill,
  FileHeart, Sparkles, Video, PackageCheck, ShieldCheck,
  CalendarDays, Wallet, TrendingUp, Globe,
  SlidersHorizontal, BellRing, ChefHat, Wrench,
  ShieldAlert, UsersRound, HeartPulse, Smartphone, Award
} from 'lucide-react';
import logo from '../../Logo/medfast-logo.png';
import { NotificationBell } from '../Notifications/NotificationCenter';
import NotificationCenter from '../Notifications/NotificationCenter';

const Header = ({ 
  currentUser, 
  networkStatus, 
  notifications, 
  deviceInfo, 
  onLogout,
  selectedFacility,
  facilitiesList,
  onFacilityChange,
  activeTab,
  availableTabs,
  onTabChange
}) => {
  const { optimization } = deviceInfo;
  const [showNotifications, setShowNotifications] = useState(false);
  const toggleNotifications = useCallback(() => setShowNotifications((v) => !v), []);

  const iconMap = {
    dashboard: LayoutDashboard,
    residents: HeartHandshake,
    enquiries: UserRoundPlus,
    clinical: Stethoscope,
    medication: Pill,
    careplans: FileHeart,
    wellbeing: Sparkles,
    telehealth: Video,
    orders: PackageCheck,
    quality: ShieldCheck,
    roster: CalendarDays,
    billing: Wallet,
    reports: TrendingUp,
    network: Globe,
    alerts: BellRing,
    kitchen: ChefHat,
    maintenance: Wrench,
    emergency: ShieldAlert,
    visitors: UsersRound,
    familyportal: HeartPulse,
    mobileapp: Smartphone,
    hr: Award,
    settings: SlidersHorizontal,
  };

  const iconColorMap = {
    dashboard:    'text-indigo-600',
    residents:    'text-rose-500',
    enquiries:    'text-sky-500',
    clinical:     'text-emerald-600',
    medication:   'text-amber-500',
    careplans:    'text-pink-500',
    wellbeing:    'text-yellow-500',
    telehealth:   'text-cyan-600',
    orders:       'text-orange-500',
    quality:      'text-teal-600',
    roster:       'text-blue-500',
    billing:      'text-green-600',
    reports:      'text-violet-500',
    network:      'text-slate-500',
    alerts:       'text-red-500',
    kitchen:      'text-orange-600',
    maintenance:  'text-stone-500',
    emergency:    'text-red-600',
    visitors:     'text-sky-600',
    familyportal: 'text-rose-400',
    mobileapp:    'text-indigo-500',
    hr:           'text-purple-500',
    settings:     'text-gray-500',
  };

  const TAB_ROW_PRIMARY = ['dashboard', 'residents', 'enquiries', 'clinical', 'medication', 'careplans', 'wellbeing', 'telehealth'];
  const TAB_ROW_SECONDARY = ['orders', 'quality', 'roster', 'billing', 'reports', 'network', 'alerts', 'settings'];
  const TAB_ROW_TERTIARY = ['kitchen', 'maintenance', 'emergency', 'visitors', 'familyportal', 'mobileapp', 'hr'];

  const renderTabButton = (tab) => {
    const Icon = iconMap[tab.id];
    if (!Icon) return null;
    const isActive = activeTab === tab.id;
    const iconColor = iconColorMap[tab.id] ?? 'text-gray-500';
    const hasAlerts =
      tab.id === 'alerts' &&
      notifications?.filter((n) => n.status === 'Active' && n.priority === 'High').length > 0;

    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => onTabChange(tab.id)}
        className={`touch-target inline-flex flex-1 items-center justify-center gap-2 px-3 py-2.5 rounded-xl transition-all text-sm font-semibold leading-tight overflow-hidden ${
          isActive
            ? 'bg-purple-50 text-purple-800 border-2 border-purple-300 shadow-sm'
            : 'text-gray-700 hover:bg-gray-50 border-2 border-transparent'
        }`}
        title={tab.label}
        style={{ minWidth: 0, zoom: 1 }}
      >
        <Icon className={`h-6 w-6 shrink-0 transition-colors ${iconColor}`} strokeWidth={1.75} style={{ zoom: 1 }} />
        <span className="hidden min-w-0 truncate text-center whitespace-nowrap sm:inline" style={{ zoom: 1 }}>{tab.label}</span>
        {hasAlerts && <span className="h-3 w-3 shrink-0 rounded-full bg-red-500 animate-pulse" aria-hidden style={{ zoom: 1 }} />}
      </button>
    );
  };

  const tabsRow1 = availableTabs.filter((t) => TAB_ROW_PRIMARY.includes(t.id));
  const tabsRow2 = availableTabs.filter((t) => TAB_ROW_SECONDARY.includes(t.id));
  const tabsRow3 = availableTabs.filter((t) => TAB_ROW_TERTIARY.includes(t.id));
  const hasFacilities = Array.isArray(facilitiesList) && facilitiesList.length > 0;

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Header] layout', {
      tabsRow1: tabsRow1.length,
      tabsRow2: tabsRow2.length,
      tabsRow3: tabsRow3.length,
      hasFacilities,
      });
    }
  }, [tabsRow1.length, tabsRow2.length, tabsRow3.length, hasFacilities]);

  return (
    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-2 shadow-sm">
      {/* Logo + facility stack on the left so we do not get a full-width empty row under the nav */}
      <div className="flex items-start justify-between gap-4 lg:gap-6">
        <div className="flex shrink-0 flex-col gap-1.5 sm:gap-2">
          <div className="relative h-16 w-[min(100%,260px)] shrink-0 overflow-visible sm:h-[4.5rem] sm:w-[280px]">
            <img
              src={logo}
              alt="MedFast Aged Care"
              className="absolute left-0 top-1/2 h-full max-h-16 w-auto max-w-full -translate-y-1/2 object-contain object-left sm:max-h-[4.5rem]"
              onLoad={() => {
                if (process.env.NODE_ENV === 'development') {
                  console.debug('[Header] Logo loaded');
                }
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[Header] Logo image failed to load');
                }
              }}
            />
          </div>
          {hasFacilities && (
            <div className="flex max-w-[min(100%,320px)] flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Facility</span>
              <select
                value={selectedFacility}
                onChange={(e) => onFacilityChange(e.target.value)}
                className="touch-target min-h-[40px] w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 sm:min-h-[44px] sm:w-auto sm:min-w-[200px]"
                title="Select Facility"
              >
                {facilitiesList.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
          <div className="flex w-full flex-wrap items-stretch gap-2 lg:flex-nowrap">
            {tabsRow1.map(renderTabButton)}
          </div>
          <div className="flex w-full flex-wrap items-stretch gap-2 lg:flex-nowrap">
            {tabsRow2.map(renderTabButton)}
          </div>
          {tabsRow3.length > 0 && (
            <div className="flex w-full flex-wrap items-stretch gap-2 lg:flex-nowrap">
              {tabsRow3.map(renderTabButton)}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            {networkStatus === 'connected' ? (
              <>
                <Wifi className={`${optimization.iconSize} text-green-500`} />
                <span>Connected</span>
              </>
            ) : (
              <>
                <WifiOff className={`${optimization.iconSize} text-yellow-500 animate-pulse`} />
                <span>Syncing</span>
              </>
            )}
          </div>
          <NotificationBell onClick={toggleNotifications} />
          {showNotifications && (
            <NotificationCenter onClose={() => setShowNotifications(false)} />
          )}
          <div className={`${optimization.fontSize} text-right text-gray-600`}>
            <p className="font-medium leading-tight">{currentUser?.name}</p>
            <p className="text-xs text-gray-500">{currentUser?.title}</p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            title="Logout"
          >
            <LogOut className={optimization.iconSize} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Header;
